/**
 * Group a plan's visible procedures for display.
 *
 * Extracted from BuilderLeftPane so the Builder's LEFT pane and its RIGHT
 * "Reasons" tab group identically. They already rendered the same array
 * (`builderVisibleProcedures`) — but the left pane grouped it by priority with
 * headers while the Reasons tab rendered it in raw drag order. On a 38-procedure
 * plan that made procedures effectively unfindable in the Reasons tab: a TC
 * looked for the extractions where the left pane puts them, didn't see them, and
 * reported them as missing. They were there, at positions ~33 and ~54.
 *
 * Pure function over (procedures, groupBy, priorities) — no store access — so
 * both panes stay in step by construction rather than by discipline.
 *
 * Returns [{ key, label, items, total }] in a stable display order.
 */

const QUADRANT_BY_TOOTH = (toothNum) => {
  const n = parseInt(toothNum, 10);
  if (!n || isNaN(n)) return 'Other';
  if (n >= 1 && n <= 8) return 'UR';
  if (n >= 9 && n <= 16) return 'UL';
  if (n >= 17 && n <= 24) return 'LL';
  if (n >= 25 && n <= 32) return 'LR';
  return 'Other';
};

const PHASE_BY_CODE = (procCode) => {
  if (!procCode) return 'Other';
  const c = procCode.toUpperCase();
  if (c.startsWith('D2')) return 'Restorative';
  if (c.startsWith('D3')) return 'Endodontics';
  if (c.startsWith('D4')) return 'Periodontics';
  if (c.startsWith('D5')) return 'Prosthodontics (Removable)';
  if (c.startsWith('D6')) return 'Prosthodontics (Fixed) / Implants';
  if (c.startsWith('D7')) return 'Oral Surgery';
  if (c.startsWith('D8')) return 'Orthodontics';
  if (c.startsWith('D9')) return 'Adjunctive';
  if (c.startsWith('D0')) return 'Diagnostic';
  if (c.startsWith('D1')) return 'Preventive';
  return 'Other';
};

export { QUADRANT_BY_TOOTH, PHASE_BY_CODE };

export function groupProcedures(procedures, groupBy, priorities) {
  const procs = procedures || [];
  if (groupBy === 'none' || procs.length === 0) {
    return [{
      key: '__all__',
      label: 'All procedures',
      items: [...procs],
      total: procs.reduce((s, p) => s + (p.fee || 0), 0),
    }];
  }

  const map = new Map();

  const keyFor = (proc) => {
    if (groupBy === 'priority') return proc.priorityName || 'Unprioritized';
    if (groupBy === 'phase') return PHASE_BY_CODE(proc.procCode);
    if (groupBy === 'quadrant') return QUADRANT_BY_TOOTH(proc.toothNum);
    if (groupBy === 'tooth') return proc.toothNum ? `Tooth #${proc.toothNum}` : 'No tooth';
    return 'All';
  };

  // Establish stable group order
  // For priority, iterate in priorities[] ItemOrder
  const orderedKeys = [];
  if (groupBy === 'priority') {
    (priorities || []).forEach((p) => { if (!orderedKeys.includes(p.itemName)) orderedKeys.push(p.itemName); });
    if (!orderedKeys.includes('Unprioritized')) orderedKeys.push('Unprioritized');
  }

  procs.forEach((p) => {
    const k = keyFor(p);
    if (!map.has(k)) {
      map.set(k, []);
      if (!orderedKeys.includes(k)) orderedKeys.push(k);
    }
    map.get(k).push(p);
  });

  return orderedKeys
    .filter((k) => map.has(k))
    .map((k) => {
      const items = map.get(k);
      return {
        key: k,
        label: k,
        items,
        total: items.reduce((s, p) => s + (p.fee || 0), 0),
      };
    });
}
